// src/components/common/FormTemplateManager.jsx
import { useEffect, useMemo, useState } from "react";
import {
  resolveTemplate,
  createTemplate,
  setDefaultTemplate,
  listTemplateVersions,
  getTemplateById,
  updateTemplate,
  deleteTemplate,
} from "../../../api/doctemp";

const parseMaybeJson = (x, fallback = {}) => {
  if (x == null) return fallback;
  if (typeof x === "string") {
    try {
      return JSON.parse(x);
    } catch {
      return fallback;
    }
  }
  if (typeof x === "object") return x;
  return fallback;
};

export default function FormTemplateManager({
  params,
  getBody,
  onApply,
  onChangeTemplate,
  autoApplyOnMount = true,
}) {
  const [serverTemplateId, setServerTemplateId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [error, setError] = useState("");

  // 저장용 템플릿 이름 입력
  const [tplTitleInput, setTplTitleInput] = useState("");

  const normParams = useMemo(() => {
    const { module, formType, mode, scope, project } = params || {};
    const normProject = () => {
      if (!project) return undefined;
      if (typeof project === "object")
        return project.id ?? project.code ?? project.pk;
      return String(project);
    };
    return {
      module,
      form_type: formType,
      mode,
      scope,
      ...(scope === "project" && normProject()
        ? { project: normProject() }
        : {}),
    };
  }, [params]);

  const emitMeta = (tpl) => {
    try {
      onChangeTemplate?.(tpl);
    } catch {}
  };

  const logAxiosError = (tag, err) => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const url = err?.config?.url;
    const method = err?.config?.method;
    console.error(
      `[${tag}] ${method?.toUpperCase?.()} ${url} -> ${status}`,
      data
    );
  };

  const ensureBody = () => {
    const b = getBody?.() || {};
    return {
      layout: Array.isArray(b.layout) ? b.layout : [],
      values: {
        title: b?.values?.title ?? "",
        editorHtml: b?.values?.editorHtml ?? "<p></p>",
        note: b?.values?.note ?? "",
        photos: Array.isArray(b?.values?.photos) ? b.values.photos : [],
      },
      blocksMeta: typeof b?.blocksMeta === "object" ? b.blocksMeta : {},
      customValues: typeof b?.customValues === "object" ? b.customValues : {},
    };
  };

  // 버전 목록 로드(선택값 우선 설정 가능)
  const fetchVersions = async (preferId = null) => {
    setLoadingVersions(true);
    try {
      const raw = await listTemplateVersions(normParams);
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
        ? raw.results
        : [];
      setVersions(arr);
      const nextId =
        preferId ??
        arr.find((v) => v.id === serverTemplateId)?.id ??
        arr[0]?.id ??
        null;
      setSelectedVersionId(nextId);
      if (arr.length && nextId) {
        const current = arr.find((v) => v.id === nextId);
        if (current?.title) setTplTitleInput(current.title);
      }
    } catch (e) {
      logAxiosError("list-versions", e);
    } finally {
      setLoadingVersions(false);
    }
  };

  // 기본(기본지정) 템플릿 불러와 즉시 적용
  const loadResolved = async () => {
    setLoading(true);
    setError("");
    try {
      const tpl = await resolveTemplate(normParams);
      setServerTemplateId(tpl.id ?? null);

      const body = parseMaybeJson(tpl.body_json, {});
      onApply?.(body, tpl);
      emitMeta(tpl);

      const fallbackTitle = body?.values?.title || "사용자 템플릿";
      setTplTitleInput(tpl.title || fallbackTitle);

      await fetchVersions(tpl.id ?? null);
    } catch (e) {
      logAxiosError("resolve", e);
      if (e?.response?.status === 404) {
        // 없으면 자동 생성 후 적용
        try {
          const eb = ensureBody();
          const payload = {
            ...normParams,
            title: "사용자 템플릿",
            description: "",
            body_json: eb,
            is_active: true,
            is_default: true,
          };
          const created = await createTemplate(payload);
          await setDefaultTemplate(created.id);
          setServerTemplateId(created.id);

          const tpl2 = await resolveTemplate(normParams);
          const body2 = parseMaybeJson(tpl2.body_json, {});
          onApply?.(body2, tpl2);
          emitMeta(tpl2);

          setTplTitleInput(tpl2.title || eb.values.title || "사용자 템플릿");

          await fetchVersions(tpl2.id ?? null);
        } catch (ce) {
          logAxiosError("auto-create", ce);
          setError(
            (ce?.response?.data && JSON.stringify(ce.response.data)) ||
              "템플릿 자동 생성 실패"
          );
        }
      } else {
        setError(
          (e?.response?.data && JSON.stringify(e.response.data)) ||
            "템플릿 불러오기 실패"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // 선택한 버전을 즉시 적용 (불러오기 버튼 없이)
  const handleSelectVersion = async (id) => {
    setSelectedVersionId(id);
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const idNum = Number(id);
      const tpl = await getTemplateById(idNum);
      setServerTemplateId(tpl?.id ?? null);

      const body = parseMaybeJson(tpl?.body_json, {});
      onApply?.(body, tpl);
      emitMeta(tpl);

      const fallbackTitle = body?.values?.title || "사용자 템플릿";
      setTplTitleInput(tpl?.title || fallbackTitle);
    } catch (e) {
      logAxiosError("get-by-id", e);
      setError(
        (e?.response?.data && JSON.stringify(e.response.data)) ||
          "버전 적용 실패"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveNewVersion = async ({ setDefault = false } = {}) => {
    setSaving(true);
    setError("");
    try {
      const eb = ensureBody();
      const titleFromInput = (tplTitleInput || "").trim();
      const payload = {
        ...normParams,
        title: titleFromInput || eb.values.title || "사용자 템플릿",
        description: eb.values.note ? String(eb.values.note).slice(0, 120) : "",
        body_json: eb,
        is_active: true,
        is_default: !!setDefault,
      };
      const created = await createTemplate(payload);
      setServerTemplateId(created.id);
      if (setDefault) {
        await setDefaultTemplate(created.id);
        await loadResolved();
      }
      emitMeta(created);
      await fetchVersions(created.id);
      alert("저장했습니다.");
    } catch (e) {
      logAxiosError("save", e);
      setError(
        (e?.response?.data && JSON.stringify(e.response.data)) || "저장 실패"
      );
    } finally {
      setSaving(false);
    }
  };

  const setAsDefault = async () => {
    if (!serverTemplateId) return saveNewVersion({ setDefault: true });
    setSaving(true);
    setError("");
    try {
      const res = await setDefaultTemplate(serverTemplateId);
      emitMeta(res);
      await fetchVersions(serverTemplateId);
      await loadResolved();
      alert("기본 템플릿으로 지정했습니다.");
    } catch (e) {
      logAxiosError("set-default", e);
      setError(
        (e?.response?.data && JSON.stringify(e.response.data)) ||
          "기본 지정 실패"
      );
    } finally {
      setSaving(false);
    }
  };

  const renameCurrent = async (newTitle) => {
    if (!serverTemplateId) return;
    try {
      // ⚠️ 서버가 is_default를 요구한다면 updateTemplate 호출 시 포함해 주세요.
      await updateTemplate(serverTemplateId, { title: newTitle });
      await fetchVersions(serverTemplateId);
    } catch (e) {
      logAxiosError("rename", e);
      setError(
        (e?.response?.data && JSON.stringify(e.response.data)) ||
          "이름 변경 실패"
      );
    }
  };

  const removeCurrent = async () => {
    if (!serverTemplateId) return;
    if (
      !window.confirm("현재 템플릿을 삭제할까요? 이 작업은 되돌릴 수 없습니다.")
    )
      return;
    try {
      await deleteTemplate(serverTemplateId);
      setServerTemplateId(null);
      await fetchVersions(null);
      if (autoApplyOnMount) {
        await loadResolved();
      }
    } catch (e) {
      logAxiosError("delete", e);
      alert("삭제 실패");
    }
  };

  useEffect(() => {
    setServerTemplateId(null);
    setVersions([]);
    setSelectedVersionId(null);
    setTplTitleInput("");
    if (autoApplyOnMount) {
      loadResolved(); // 자동적용 켜진 경우에만 템플릿 즉시 적용
    } else {
      fetchVersions(null); // 자동적용 끈 경우: 목록만 불러옴
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(normParams)]);

  return (
    <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      {/* 좌측: 버전 드롭다운 & 기타 액션 */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={selectedVersionId ?? ""}
          onChange={(e) => handleSelectVersion(e.target.value || null)}
          className="min-w-[260px] px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900"
          title="버전을 선택하면 즉시 적용됩니다"
        >
          {versions.length === 0 && <option value="">(버전 없음)</option>}
          {versions.map((v) => (
            <option key={v.id} value={v.id}>
              {v.title || "제목없음"}
              {v.is_default ? " · 기본" : ""}
              {v.version ? ` · v${v.version}` : ""}
              {v.created_at
                ? ` · ${new Date(v.created_at).toLocaleString()}`
                : ""}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => fetchVersions(selectedVersionId)}
          disabled={loadingVersions}
          className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 disabled:opacity-60"
          title="버전 목록 새로고침"
        >
          새로고침
        </button>

        <button
          type="button"
          onClick={loadResolved}
          disabled={loading}
          className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 disabled:opacity-60"
          title="기본(기본지정된) 템플릿 다시 적용"
        >
          기본 불러오기
        </button>
      </div>

      {/* 우측: 템플릿 이름 입력 + 저장/기본지정/이름변경/삭제 */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={tplTitleInput}
          onChange={(e) => setTplTitleInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              saveNewVersion();
            }
          }}
          placeholder="템플릿 이름"
          title="저장 시 사용할 템플릿 이름"
          className="min-w-[200px] px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900"
        />

        <button
          type="button"
          onClick={() => saveNewVersion()}
          disabled={saving}
          className="px-3 py-2 rounded-xl border border-transparent bg-blue-600 text-white disabled:opacity-60"
          title="현재 입력한 이름으로 새 버전 저장"
        >
          저장
        </button>

        <button
          type="button"
          onClick={setAsDefault}
          disabled={saving}
          className="px-3 py-2 rounded-xl border border-transparent bg-blue-600 text-white disabled:opacity-60"
          title="현재 버전을 기본 템플릿으로 지정"
        >
          기본 지정
        </button>

        <button
          type="button"
          onClick={() => {
            const name = (tplTitleInput || "").trim();
            if (!serverTemplateId) return alert("변경할 템플릿이 없습니다.");
            if (!name) return alert("템플릿 이름을 입력하세요.");
            renameCurrent(name);
          }}
          className="px-3 py-2 rounded-xl border border-transparent bg-blue-600 text-white"
          title="현재 템플릿의 제목만 변경"
        >
          이름 변경
        </button>

        <button
          type="button"
          onClick={removeCurrent}
          className="px-3 py-2 rounded-xl border border-transparent bg-blue-600 text-white"
          title="현재 템플릿 삭제"
        >
          삭제
        </button>
      </div>

      {error && <div className="w-full text-sm text-red-600">{error}</div>}
    </div>
  );
}
